<template>
  <div
    class="jitsi-layer"
    :class="{ minimized: isMinimized }"
  >
    <section
      ref="fullscreenRef"
      class="jitsi-panel"
      :class="{
        maximized: isMaximized,
        minimized: isMinimized
      }"
      role="dialog"
      aria-modal="true"
    >
      <header class="jitsi-header">
        <div class="jitsi-title">
          {{ data.subject || $t("MEETING") }}
        </div>

        <div class="jitsi-actions">
          <!-- Minimize / restore -->
          <b-button
            class="no-border"
            size="sm"
            @click="toggleMinimize"
            :title="isMinimized ? $t('RESTORE') : $t('MINIMIZE')"
          >
            <i
              :class="
                isMinimized
                  ? 'fa-solid fa-up-right-and-down-left-from-center'
                  : 'fa-solid fa-minus'
              "
            ></i>
          </b-button>

          <!-- Maximize -->
          <b-button
            v-if="!isMinimized"
            class="no-border"
            size="sm"
            @click="toggleMaximize"
            :title="isMaximized ? $t('EXIT_FULL_SCREEN') : $t('MAXIMIZE')"
          >
            <i
              :class="
                isMaximized
                  ? 'fa-solid fa-down-left-and-up-right-to-center'
                  : 'fa-solid fa-up-right-and-down-left-from-center'
              "
            ></i>
          </b-button>

          <!-- End meeting -->
          <b-button
            class="no-border"
            size="sm"
            @click="closeMeeting(false)"
            :title="$t('END_MEETING')"
          >
            <i class="fa-solid fa-xmark"></i>
          </b-button>
        </div>
      </header>

      <!-- 
        Keep this mounted at all times.
        The iframe is still visible when minimized, just smaller.
      -->
      <div class="jitsi-body">
        <div
          ref="jitsiContainerRef"
          class="jitsi-container"
        ></div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "JitsiIframe",

  props: {
    data: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isMaximized: false,
      isMinimized: false
    };
  },

  created() {
    this.jitsiApi = null;
    this.hasClosed = false;
  },

  mounted() {
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    this.startMeeting();
  },

  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    this.disposeMeeting();
  },

  methods: {
    loadJitsiScript(domain) {
      const scriptId = "jitsi-external-api";

      if (window.JitsiMeetExternalAPI) {
        return Promise.resolve();
      }

      const existingScript = document.getElementById(scriptId);

      if (existingScript) {
        return new Promise((resolve, reject) => {
          existingScript.addEventListener("load", resolve);
          existingScript.addEventListener("error", reject);
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");

        script.id = scriptId;
        script.src = `https://${domain}/external_api.js`;
        script.async = true;

        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load Jitsi API"));

        document.body.appendChild(script);
      });
    },

    async startMeeting() {
      if (!this.data || !this.data.server || !this.data.room || !this.data.token) {
        console.error("Missing Jitsi meeting data", this.data);
        return;
      }

      try {
        await this.loadJitsiScript(this.data.server);

        if (!this.$refs.jitsiContainerRef) {
          return;
        }

        this.disposeMeeting();

        this.hasClosed = false;
        this.isMinimized = false;
        this.isMaximized = false;

        const options = {
          roomName: this.data.room,
          jwt: this.data.token,
          parentNode: this.$refs.jitsiContainerRef,
          width: "100%",
          height: "100%",

          configOverwrite: {
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            subject: this.data.subject,

            toolbarButtons: [
              "microphone",
              "camera",
              "desktop",
              "fullscreen",
              "hangup",
              "chat",
              "participants-pane",
              "tileview",
              "settings"
            ]
          },

          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false
          }
        };

        const api = new window.JitsiMeetExternalAPI(this.data.server, options);

        this.jitsiApi = api;

        api.addListener("readyToClose", this.handleJitsiClose);
        //api.addListener("videoConferenceLeft", this.handleJitsiClose);
        api.addListener("participantKickedOut", this.handleKickedOut);

        this.$nextTick(() => {
          this.patchJitsiIframePermissions();
        });
      } catch (error) {
        console.error("Failed to start Jitsi meeting", error);

        if (this.$noty) {
          this.$noty.error(this.$t("MEETING_FAILED_TO_START"));
        }
      }
    },

    patchJitsiIframePermissions() {
      const container = this.$refs.jitsiContainerRef;

      if (!container) {
        return;
      }

      const iframe = container.querySelector("iframe");

      if (!iframe) {
        return;
      }

      iframe.setAttribute(
        "allow",
        "camera; microphone; display-capture; fullscreen; clipboard-write; autoplay"
      );

      iframe.setAttribute("allowfullscreen", "true");
    },

    minimizeMeeting() {
      this.isMinimized = true;

      if (document.fullscreenElement === this.$refs.fullscreenRef) {
        document.exitFullscreen().catch(() => {});
      }
    },

    restoreMeeting() {
      this.isMinimized = false;
    },

    toggleMinimize() {
      if (this.isMinimized) {
        this.restoreMeeting();
      } else {
        this.minimizeMeeting();
      }
    },

    async toggleMaximize() {
      const shell = this.$refs.fullscreenRef;

      if (!shell || this.isMinimized) {
        return;
      }

      try {
        if (document.fullscreenElement === shell) {
          await document.exitFullscreen();
        } else {
          await shell.requestFullscreen();
        }
      } catch (error) {
        console.warn("Fullscreen failed", error);
      }
    },

    handleFullscreenChange() {
      this.isMaximized = document.fullscreenElement === this.$refs.fullscreenRef;
    },

    handleJitsiClose() {      
      console.log("handleJitsiClose");
      this.closeMeeting(true);
      if (this.$noty) {
        this.$noty.info(this.$t("MEETING_CLOSED"));
      }
    },

    handleKickedOut(event) {
      console.log("handleKickedOut");

      // Ignore when someone else was kicked
      if (!event || !event.kicked || !event.kicked.local) {
        return;
      }
       console.log("Local user was kicked out");
    
      this.closeMeeting(true);

      if (this.$noty) {
        this.$noty.info(this.$t("MEETING_KICKED_OUT"));
      }
    },

    closeMeeting(fromJitsi = false) {
      if (this.hasClosed) {
        return;
      }

      this.hasClosed = true;

      if (!fromJitsi && this.jitsiApi) {
        try {
          this.jitsiApi.executeCommand("hangup");
        } catch (error) {
          console.warn("Jitsi hangup failed", error);
        }
      }

      if (document.fullscreenElement === this.$refs.fullscreenRef) {
        document.exitFullscreen().catch(() => {});
      }

      this.disposeMeeting();
      this.$emit("close");
    },

    disposeMeeting() {
      if (!this.jitsiApi) {
        return;
      }

      try {
        this.jitsiApi.removeListener("readyToClose", this.handleJitsiClose);
        //this.jitsiApi.removeListener("videoConferenceLeft", this.handleJitsiClose);
        this.jitsiApi.removeListener("participantKickedOut", this.handleKickedOut);

        this.jitsiApi.dispose();
      } catch (error) {
        console.warn("Jitsi dispose failed", error);
      }

      this.jitsiApi = null;
    }
  }
};
</script>

<style scoped>
.jitsi-layer {
  position: fixed;
  inset: 0;
  z-index: 1050;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 24px;
  background: rgba(0, 0, 0, 0.35);

  pointer-events: auto;
}

.jitsi-layer.minimized {
  background: transparent;
  pointer-events: none;
}

.jitsi-panel {
  width: min(1200px, 94vw);
  height: min(760px, 88dvh);

  display: flex;
  flex-direction: column;

  background: #111;
  border-radius: 14px;
  overflow: hidden;

  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);

  pointer-events: auto;
}

.jitsi-panel.maximized {
  width: 100vw;
  height: 100dvh;
  border-radius: 0;
}

.jitsi-panel.minimized {
  position: fixed;
  right: 24px;
  bottom: 24px;

  width: 360px;
  height: 240px;

  border-radius: 14px;
  overflow: hidden;
}

.jitsi-header {
  height: 48px;
  min-height: 48px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0 12px 0 16px;

  background: #1f1f1f;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.jitsi-panel.minimized .jitsi-header {
  height: 36px;
  min-height: 36px;
  padding: 0 8px 0 10px;
}

.jitsi-title {
  max-width: calc(100% - 150px);

  color: #fff;
  font-size: 14px;
  font-weight: 600;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.jitsi-panel.minimized .jitsi-title {
  max-width: 210px;
  font-size: 12px;
}

.jitsi-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.jitsi-actions i {
  color: #fff;
}

.jitsi-body {
  flex: 1;
  min-height: 0;

  background: #111;
}

.jitsi-container {
  width: 100%;
  height: 100%;
}

/*
  In minimized mode, the real Jitsi iframe remains visible.
  This gives the user meeting content instead of a dead badge.
*/
.jitsi-panel.minimized .jitsi-body {
  flex: 1;
  min-height: 0;
}

.jitsi-panel.minimized .jitsi-container {
  width: 100%;
  height: 100%;
}
</style>